import { Flex, Space, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { porteData, numeroColaboradoresData } from "@/modules/empresas/schema/empresa-schema";
import useGetProjetoById from "@/modules/projetos/hooks/useGetProjetoById";
import { getRegioesByEmpresaAndProjeto } from "@/modules/regiao/firestore";
import useUserStore from "@/modules/users/store";
import PieChartComponent from "../graphics-nivo/PieChart";
import GetEmpresas from "../graphics-nivo/useGetEmpresa";

type GraphSegment = { id: string; label: string; value: number };

const CardPies = ({ empresas }: { empresas: TConvite[] }) => {
  const { activeEmpresaId, activeProjeto } = useUserStore();

  const [formatEmpresas, setFormatEmpresas] = useState<TEmpresa[]>([]);
  // pega o projeto mas não vai entrar em loop por conta dele
  useGetProjetoById(activeEmpresaId ?? "", activeProjeto ?? "");

  const { data: regioes = [], isError: regioesError } = useQuery({
    queryKey: ["regioes", activeEmpresaId, activeProjeto],
    queryFn: async () => {
      if (!activeEmpresaId || !activeProjeto) {
        throw new Error("IDs obrigatórios ausentes");
      }
      const result = await getRegioesByEmpresaAndProjeto(activeEmpresaId, activeProjeto);
      return result.docs.map((document) => document.data());
    },
    enabled: !!activeEmpresaId && !!activeProjeto,
  });

  const loadDataEmpresa = useCallback(async () => {
    if (empresas.length === 0) {
      setFormatEmpresas([]);
      return;
    }
    try {
      const newEmpresas = await GetEmpresas(empresas);
      setFormatEmpresas(newEmpresas);
    } catch (error) {
      console.error("CardPies: Falha ao carregar dados de empresas", error);
      setFormatEmpresas([]);
    }
  }, [empresas]);

  useEffect(() => {
    void loadDataEmpresa();
  }, [loadDataEmpresa]);

  const formatRegioes = useMemo<GraphSegment[]>(
    () =>
      regioes.map((r) => ({
        id: r.name,
        label: r.name,
        value: r.empresas?.length ?? 0
      })),
    [regioes]
  );

  // em vez de estado+effect, só useMemo
  const formatCategorias = useMemo<GraphSegment[]>(() => {
    const segments = new Map<string, GraphSegment>();
    for (const emp of formatEmpresas) {
      const cat = emp.categorias ?? "Sem categoria";
      const seg = segments.get(cat);
      if (seg) seg.value++;
      else segments.set(cat, { id: cat, label: cat, value: 1 });
    }
    return [...segments.values()];
  }, [formatEmpresas]);

  // esses permanecem como antes, sem alterar estado
  const formatPorteData = porteData.map((item) => ({
    id: item.value,
    label: item.label,
    value: formatEmpresas.filter((error) => error.porte === item.value).length
  }));

  const formatColaboradores = numeroColaboradoresData.map((item) => ({
    id: item.value,
    label: item.label,
    value: formatEmpresas.filter((error) => error.numeroColaboradores === item.value).length
  }));

  return (
    <>
      <Text size="md" fw={700}>
        Quantidade de empresas por porte
      </Text>
      <PieChartComponent data={formatPorteData} type={1} />
      <Space h="md" />
      <Text size="md" fw={700}>
        Quantidade de empresas por número de colaboradores
      </Text>
      <PieChartComponent data={formatColaboradores} type={2} />
      <Text size="md" fw={700}>
        Quantidade de empresas por categoria
      </Text>
      <PieChartComponent data={formatCategorias} type={3} />
      <Text size="md" fw={700}>
        Quantidade de empresas por região
      </Text>
      {regioesError ? (
        <Flex justify="center" style={{ minHeight: 50 }}>
          <Text c="red">Erro ao carregar regiões</Text>
        </Flex>
      ) : formatRegioes.length > 0 ? (
        <PieChartComponent data={formatRegioes} type={4} />
      ) : (
        <Flex justify="center" style={{ minHeight: 50 }}>
          <Text>Nenhuma região cadastrada</Text>
        </Flex>
      )}
    </>
  );
};

export default CardPies;
